import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface FootprintPoint {
  lat: number;
  lng: number;
  timestamp: number;
}

export interface VehicleFootprint {
  vehicleId: string;
  vehicleName: string;
  color: string;
  points: FootprintPoint[];
  isActive: boolean;
  startPoint: FootprintPoint | null; // Punto de inicio del tracking
}

interface VehicleFootprintTrackingState {
  activeFootprints: Record<string, VehicleFootprint>;
  lastPersistTime: number;
}

interface VehicleFootprintTrackingActions {
  startFootprint: (
    vehicleId: string,
    vehicleName: string,
    color: string,
    initialLat?: number,
    initialLng?: number,
  ) => void;
  stopFootprint: (vehicleId: string) => void;
  addFootprintPoint: (vehicleId: string, lat: number, lng: number) => void;
  clearFootprint: (vehicleId: string) => void;
  setFootprintColor: (vehicleId: string, color: string) => void;
  getFootprint: (vehicleId: string) => VehicleFootprint | undefined;
  isFootprintActive: (vehicleId: string) => boolean;
  getAllActiveFootprints: () => VehicleFootprint[];
}

type VehicleFootprintTrackingStore = VehicleFootprintTrackingState &
  VehicleFootprintTrackingActions;

// Configuracion de rendimiento
const MAX_POINTS_PER_VEHICLE = 1000; // Limite de puntos por vehiculo
const MIN_DISTANCE_METERS = 5; // Distancia minima entre puntos (metros)

// Calcular distancia entre dos puntos (Haversine simplificado para cortas distancias)
const getDistanceMeters = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const R = 6371000; // Radio de la Tierra en metros
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const useVehicleFootprintTrackingStore = create<VehicleFootprintTrackingStore>()(
  persist(
    (set, get) => ({
      activeFootprints: {},
      lastPersistTime: 0,

      startFootprint: (vehicleId, vehicleName, color, initialLat?, initialLng?) => {
        const startPoint = (initialLat !== undefined && initialLng !== undefined)
          ? { lat: initialLat, lng: initialLng, timestamp: Date.now() }
          : null;

        set((state) => ({
          activeFootprints: {
            ...state.activeFootprints,
            [vehicleId]: {
              vehicleId,
              vehicleName,
              color,
              points: startPoint ? [startPoint] : [],
              isActive: true,
              startPoint,
            },
          },
        }));
      },

      stopFootprint: (vehicleId) => {
        set((state) => {
          const { [vehicleId]: _, ...rest } = state.activeFootprints;
          return { activeFootprints: rest };
        });
      },

      addFootprintPoint: (vehicleId, lat, lng) => {
        const state = get();
        const footprint = state.activeFootprints[vehicleId];
        if (!footprint || !footprint.isActive) return;

        const lastPoint = footprint.points[footprint.points.length - 1];
        const newPoint = { lat, lng, timestamp: Date.now() };

        // Verificar distancia minima para evitar puntos redundantes
        if (lastPoint) {
          const distance = getDistanceMeters(
            lastPoint.lat,
            lastPoint.lng,
            lat,
            lng,
          );
          if (distance < MIN_DISTANCE_METERS) return;
        }

        // Crear nuevo array de puntos, limitando el tamano
        let newPoints = [
          ...footprint.points,
          newPoint,
        ];

        // Si excede el limite, remover los puntos mas antiguos
        if (newPoints.length > MAX_POINTS_PER_VEHICLE) {
          newPoints = newPoints.slice(-MAX_POINTS_PER_VEHICLE);
        }

        // Si no hay startPoint, establecer el primer punto como startPoint
        const startPoint = footprint.startPoint || (footprint.points.length === 0 ? newPoint : null);

        set((state) => ({
          activeFootprints: {
            ...state.activeFootprints,
            [vehicleId]: {
              ...footprint,
              points: newPoints,
              startPoint: startPoint || footprint.startPoint,
            },
          },
        }));
      },

      clearFootprint: (vehicleId) => {
        set((state) => {
          const footprint = state.activeFootprints[vehicleId];
          if (!footprint) return state;

          return {
            activeFootprints: {
              ...state.activeFootprints,
              [vehicleId]: {
                ...footprint,
                points: [],
                startPoint: null,
              },
            },
          };
        });
      },

      setFootprintColor: (vehicleId, color) => {
        set((state) => {
          const footprint = state.activeFootprints[vehicleId];
          if (!footprint) return state;

          return {
            activeFootprints: {
              ...state.activeFootprints,
              [vehicleId]: {
                ...footprint,
                color,
              },
            },
          };
        });
      },

      getFootprint: (vehicleId) => {
        return get().activeFootprints[vehicleId] ?? undefined;
      },

      isFootprintActive: (vehicleId) => {
        return vehicleId in get().activeFootprints;
      },

      getAllActiveFootprints: () => {
        return Object.values(get().activeFootprints);
      },
    }),
    {
      name: "vehicle-footprint-tracking",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeFootprints: state.activeFootprints,
      }),
    },
  ),
);

// Selector para obtener el objeto de footprints (referencia estable)
export const useActiveFootprintsMap = () =>
  useVehicleFootprintTrackingStore((state) => state.activeFootprints);

export const useIsFootprintActive = (vehicleId: string) =>
  useVehicleFootprintTrackingStore((state) => vehicleId in state.activeFootprints);

export const useFootprintPointsCount = (vehicleId: string) =>
  useVehicleFootprintTrackingStore(
    (state) => state.activeFootprints[vehicleId]?.points.length ?? 0,
  );

// Tipo para datos de polyline en el mapa
export interface FootprintForMap {
  vehicleId: string;
  color: string;
  points: FootprintPoint[];
  startPoint: FootprintPoint | null;
}

// Selector que retorna el objeto de footprints directamente (referencia estable)
export const useActiveFootprintsRaw = () =>
  useVehicleFootprintTrackingStore((state) => state.activeFootprints);
